import { Menu, Radio, LayoutDashboard, Newspaper } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";

function isTabActive(pathname, href, { exact = false } = {}) {
  if (!href) return false;
  if (exact || href === "/profile" || href === "/live" || href === "/updates") {
    return pathname === href || (href === "/updates" && pathname.startsWith("/updates/"));
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Mobile bottom navigation — app-like tab bar (hidden on lg+).
 * Primary destinations from the user's menu + Menu (opens sidebar).
 */
export function BottomNav({ onMenuClick }) {
  const location = useLocation();
  const { menuItems = [], homePath, portal } = useAuth();
  const pathname = location.pathname;

  const primary = [];
  const seen = new Set();

  const pushItem = (item, extras = {}) => {
    if (!item?.href || seen.has(item.href)) return;
    seen.add(item.href);
    primary.push({ ...item, ...extras });
  };

  const homeItem =
    menuItems.find((i) => i.href === homePath) ||
    menuItems.find((i) => i.label === "Dashboard") ||
    null;

  if (homeItem) {
    pushItem(homeItem, {
      label: "Home",
      exact: true,
      icon: homeItem.icon || LayoutDashboard,
    });
  } else if (homePath) {
    pushItem({
      href: homePath,
      label: "Home",
      icon: LayoutDashboard,
      exact: true,
    });
  }

  if (portal === "member") {
    pushItem({ href: "/updates", label: "Updates", icon: Newspaper, exact: true });
    pushItem({ href: "/live", label: "Live", icon: Radio, exact: true });
  }

  for (const item of menuItems) {
    if (primary.length >= 3) break;
    if (item.children?.length) continue;
    if (!item.href) continue;
    if (item.href === "/profile" || item.href === "/live" || item.href === "/updates") continue;
    pushItem(item);
  }

  const profileItem = menuItems.find((i) => i.href === "/profile");
  if (profileItem && primary.length < 4) {
    pushItem(profileItem, { exact: true });
  }

  const tabs = primary.slice(0, 4);

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-md shadow-[0_-4px_24px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      <div className="flex items-stretch justify-around min-h-16 px-1">
        {tabs.map((item) => {
          const Icon = item.icon || LayoutDashboard;
          const active = isTabActive(pathname, item.href, { exact: item.exact });
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 min-h-14 rounded-lg transition-colors",
                active ? "text-[#1E74FD]" : "text-muted-foreground active:text-foreground"
              )}
            >
              <Icon className={cn("h-5 w-5", active && "stroke-[2.25]")} aria-hidden />
              <span
                className={cn(
                  "text-[10px] font-medium leading-tight truncate max-w-full",
                  active && "font-semibold"
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={onMenuClick}
          className="flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 min-h-14 rounded-lg text-muted-foreground active:text-foreground transition-colors"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" aria-hidden />
          <span className="text-[10px] font-medium leading-tight">Menu</span>
        </button>
      </div>
    </nav>
  );
}
